import { useDarkMode } from '@/contexts/DarkModeContext';
import {
  Background,
  type Connection,
  type Edge,
  type EdgeChange,
  type IsValidConnection,
  MiniMap,
  type Node,
  type NodeChange,
  type NodeTypes,
  type OnConnectStart,
  ReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { CanvasControls } from './CanvasControls';

// Enhanced edge styling with better directionality and thickness
const StyledFlowCanvas = styled.div<{ $interactive: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 300px;
  position: relative;
  contain: layout;

  /* Enhanced edge styling with new color palette */
  .react-flow__edge-path {
    stroke-width: 3px;
    stroke: #718096;
    fill: none;
  }

  /* Larger, more prominent arrow markers */
  .react-flow__arrowhead {
    fill: #4a5568;
    stroke: #4a5568;
    stroke-width: 1px;
  }

  /* Custom arrow marker with better visibility */
  .react-flow__edge.react-flow__edge-default .react-flow__edge-path {
    stroke-width: 3px;
    stroke: #718096;
    stroke-dasharray: none;
  }

  /* Hover effects for edges - only in interactive mode */
  ${({ $interactive }) =>
    $interactive &&
    `
    .react-flow__edge:hover .react-flow__edge-path {
      stroke: #4a5568;
      stroke-width: 4px;
    }

    .react-flow__edge:hover .react-flow__arrowhead {
      fill: #4a5568;
      stroke: #4a5568;
    }
  `}

  /* Selected edge styling */
  .react-flow__edge.selected .react-flow__edge-path {
    stroke: #3182ce;
    stroke-width: 4px;
  }

  .react-flow__edge.selected .react-flow__arrowhead {
    fill: #3182ce;
    stroke: #3182ce;
  }

  /* Enhanced arrow markers */
  .react-flow__arrowhead {
    width: 12px;
    height: 12px;
  }

  /* Override React Flow's default node drag cursor */
  .react-flow__node {
    cursor: pointer !important;
  }

  /* Ensure drag handle shows correct cursor */
  .react-flow__node .drag-handle {
    cursor: grab !important;
  }

  .react-flow__node .drag-handle:active {
    cursor: grabbing !important;
  }

  /* 
   * SEMICIRCLE HANDLE IMPLEMENTATION
   * ================================
   * 
   * Problem: We want handles that look like semicircles (clean design) but have 
   * full circular hit areas (better UX). React Flow doesn't support this natively.
   * 
   * Solution: Two-layer approach:
   * 1. Main handle element: Invisible full circle for interaction
   * 2. ::before pseudo-element: Visible semicircle for appearance
   * 
   * Why this is complex:
   * - Can't use clip-path (makes clipped areas non-interactive)
   * - Can't use overflow:hidden (same problem)
   * - Must use pseudo-elements with pointer-events:none
   */

  /* Layer 1: Invisible full circular handle for interaction */
  .react-flow__handle {
    width: 14px !important;
    height: 14px !important;
    background: transparent !important; /* Completely invisible */
    border: none !important; /* No visual styling */
    position: absolute !important;
    transition: all 0.2s ease !important;
    cursor: crosshair !important;
    z-index: 10 !important;
    border-radius: 50% !important; /* Full circle hit area */
  }

  /* Layer 2: Visual semicircle using pseudo-element */
  .react-flow__handle::before {
    content: '' !important;
    position: absolute !important;
    width: 50% !important; /* Half the width = semicircle */
    height: 100% !important;
    border: 1px solid #666 !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15) !important;
    pointer-events: none !important; /* CRITICAL: Don't interfere with interaction */
  }

  /* Source handles (right side of nodes) */
  .react-flow__handle.source {
    right: -8px !important; /* Position 1px outside node border */
  }

  .react-flow__handle.source::before {
    right: 0 !important; /* Semicircle on right half of circle */
    top: 0 !important;
    background: #4a5568 !important; /* Gray color for source handles */
    border-left: none !important; /* No border on flat edge */
    border-radius: 0 50% 50% 0 !important; /* Right semicircle shape */
  }

  /* Target handles (left side of nodes) */
  .react-flow__handle.target {
    left: -8px !important; /* Position 1px outside node border */
  }

  .react-flow__handle.target::before {
    left: 0 !important; /* Semicircle on left half of circle */
    top: 0 !important;
    background: #3182ce !important; /* Blue color for target handles */
    border-right: none !important; /* No border on flat edge */
    border-radius: 50% 0 0 50% !important; /* Left semicircle shape */
  }

  /* HOVER STATES - Grow both layers simultaneously */
  /* Only apply hover states in interactive mode */
  ${({ $interactive }) =>
    $interactive &&
    `
    /* Expand invisible interaction circle */
    .react-flow__handle:hover {
      width: 16px !important; /* Larger hit area on hover */
      height: 16px !important;
    }

    /* Move handles further out to account for larger size */
    .react-flow__handle.source:hover {
      right: -11px !important; /* 3px further out for smooth animation */
    }

    .react-flow__handle.target:hover {
      left: -11px !important; /* 3px further out for smooth animation */
    }

    /* Enhanced visual styling for visible semicircle on hover */
    .react-flow__handle:hover::before {
      border-width: 2px !important; /* Thicker border */
      box-shadow:
        0 0 0 2px rgba(49, 130, 206, 0.2),
        0 2px 6px rgba(0, 0, 0, 0.3) !important;
    }

    /* Type-specific hover colors */
    .react-flow__handle.source:hover::before {
      border-color: #2d3748 !important; /* Darker gray for source */
      box-shadow:
        0 0 0 2px rgba(45, 55, 72, 0.2),
        0 2px 6px rgba(0, 0, 0, 0.3) !important;
    }

    .react-flow__handle.target:hover::before {
      border-color: #2c5282 !important; /* Darker blue for target */
      box-shadow:
        0 0 0 2px rgba(49, 130, 206, 0.2),
        0 2px 6px rgba(0, 0, 0, 0.3) !important;
    }
  `}

  /* Connection target visual indicators - semicircle shape */
  .react-flow__handle.handle-valid-target {
    width: 10px !important;
    height: 14px !important;
    border: 2px solid #38a169 !important;
    box-shadow: 0 0 6px rgba(56, 161, 105, 0.6) !important;
    animation: pulse-valid 1.5s infinite;
    background: linear-gradient(135deg, #38a169 0%, #2f855a 100%) !important;
    z-index: 15 !important;
  }

  .react-flow__handle.source.handle-valid-target {
    right: -10px !important;
    border-radius: 0 10px 10px 0 !important;
    border-left: none !important;
  }

  .react-flow__handle.target.handle-valid-target {
    left: -10px !important;
    border-radius: 10px 0 0 10px !important;
    border-right: none !important;
  }

  .react-flow__handle.handle-invalid-target {
    width: 10px !important;
    height: 10px !important;
    opacity: 0.3 !important;
    border-color: #e53e3e !important;
    background: #f5f5f5 !important;
    cursor: not-allowed !important;
  }

  /* Config port styling - orange/amber theme */
  .react-flow__handle.handle-config-port::before {
    background: #f59e0b !important; /* Amber color for config ports */
    border-color: #d97706 !important; /* Darker amber for border */
  }

  /* Config port hover states - only in interactive mode */
  ${({ $interactive }) =>
    $interactive &&
    `
    .react-flow__handle.handle-config-port:hover::before {
      background: #d97706 !important; /* Darker amber on hover */
      border-color: #92400e !important; /* Even darker amber for border */
      box-shadow:
        0 0 0 2px rgba(245, 158, 11, 0.3),
        0 2px 6px rgba(0, 0, 0, 0.3) !important;
    }
  `}

  /* Pulse animation for valid targets - updated colors */
  @keyframes pulse-valid {
    0% {
      box-shadow: 0 0 8px rgba(56, 161, 105, 0.6);
    }
    50% {
      box-shadow: 0 0 16px rgba(56, 161, 105, 0.8);
    }
    100% {
      box-shadow: 0 0 8px rgba(56, 161, 105, 0.6);
    }
  }

  /* MiniMap Styling */
  .react-flow__minimap {
    /* Enhanced shadow for better visibility */
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.15),
      0 2px 4px rgba(0, 0, 0, 0.1);
    /* Smooth transitions */
    transition: all 0.2s ease;
  }

  .react-flow__minimap:hover {
    box-shadow:
      0 6px 16px rgba(0, 0, 0, 0.2),
      0 4px 8px rgba(0, 0, 0, 0.15);
  }

  /* MiniMap node styling */
  .react-flow__minimap-node {
    /* Better node visibility in minimap */
    stroke-width: 2px;
  }

  /* MiniMap mask (viewport indicator) - Dynamic based on theme */
  .react-flow__minimap-mask {
    /* Mask colors will be set dynamically in the component */
    stroke-width: 2px;
    stroke-dasharray: 5, 5;
  }
`;

export interface FlowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  nodeTypes: NodeTypes;
  onNodesChange?: (changes: NodeChange[]) => void;
  onEdgesChange?: (changes: EdgeChange[]) => void;
  onConnect?: (connection: Connection) => void;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onEdgeClick?: (event: React.MouseEvent, edge: Edge) => void;
  onEdgeContextMenu?: (event: React.MouseEvent, edge: Edge) => void;
  onPaneClick?: (event: React.MouseEvent) => void;
  onDrop?: (event: React.DragEvent) => void;
  onDragOver?: (event: React.DragEvent) => void;
  isValidConnection?: IsValidConnection;
  interactive?: boolean;
  onAutoLayout?: () => void;
  onConnectStart?: OnConnectStart;
  onConnectEnd?: (event: MouseEvent | TouchEvent) => void;
}

const DimensionAwareFlow: React.FC<FlowCanvasProps> = ({
  nodes,
  edges,
  nodeTypes,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onEdgeClick,
  onEdgeContextMenu,
  onPaneClick,
  onDrop,
  onDragOver,
  isValidConnection,
  interactive = true,
  onAutoLayout,
  onConnectStart,
  onConnectEnd,
}) => {
  const [isDarkMode] = useDarkMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isReady, setIsReady] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidDimensions = useRef({ width: 0, height: 0 });

  // Debounced ResizeObserver to prevent infinite loops
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Clear previous debounce
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Only update if dimensions actually changed significantly
        const widthChanged =
          Math.abs(width - lastValidDimensions.current.width) > 1;
        const heightChanged =
          Math.abs(height - lastValidDimensions.current.height) > 1;

        if (widthChanged || heightChanged) {
          debounceTimeoutRef.current = setTimeout(() => {
            if (width > 0 && height > 0) {
              setDimensions({ width, height });
              lastValidDimensions.current = { width, height };
              setIsReady(true);
            }
          }, 100); // 100ms debounce
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Don't show loading state if we've already been ready once
  const shouldShowReactFlow =
    isReady && dimensions.width > 0 && dimensions.height > 0;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {shouldShowReactFlow ? (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={interactive ? onNodesChange : undefined}
          onEdgesChange={interactive ? onEdgesChange : undefined}
          onConnect={interactive ? onConnect : undefined}
          onNodeClick={interactive ? onNodeClick : undefined}
          onEdgeClick={interactive ? onEdgeClick : undefined}
          onEdgeContextMenu={interactive ? onEdgeContextMenu : undefined}
          onPaneClick={interactive ? onPaneClick : undefined}
          onPaneContextMenu={(event) => {
            // Disable right-click context menu on canvas
            event.preventDefault();
          }}
          onDrop={interactive ? onDrop : undefined}
          onDragOver={interactive ? onDragOver : undefined}
          onConnectStart={interactive ? onConnectStart : undefined}
          onConnectEnd={interactive ? onConnectEnd : undefined}
          isValidConnection={interactive ? isValidConnection : undefined}
          nodesDraggable={interactive}
          nodesConnectable={interactive}
          elementsSelectable={interactive}
          fitView
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Background />
          {interactive && (
            <MiniMap
              nodeStrokeColor={isDarkMode ? '#4f46e5' : '#3182ce'}
              nodeColor={(node) => {
                // Use the same color logic as the main nodes
                const category = node.data?.category || 'flow';
                const categoryColors = {
                  io: isDarkMode ? '#1e293b' : '#f0f4f8',
                  flow: isDarkMode ? '#1f2937' : '#f7fafc',
                  processing: isDarkMode ? '#2e1065' : '#faf5ff',
                  transform: isDarkMode ? '#064e3b' : '#f0fff4',
                  document: isDarkMode ? '#451a03' : '#fffaf0',
                };
                return (
                  categoryColors[category as keyof typeof categoryColors] ||
                  categoryColors.flow
                );
              }}
              nodeBorderRadius={6}
              maskColor={
                isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.3)'
              }
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#f8fafc',
                border: isDarkMode ? '1px solid #374151' : '1px solid #e2e8f0',
                borderRadius: '6px',
                bottom: 20,
                right: 60, // Position to the left of the controls
                width: 200,
                height: 150,
                zIndex: 10,
              }}
            />
          )}
          <CanvasControls onAutoLayout={onAutoLayout} disabled={!interactive} />
        </ReactFlow>
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '14px',
          }}
        >
          Loading visualization...
        </div>
      )}
    </div>
  );
};

export const FlowCanvas: React.FC<FlowCanvasProps> = React.memo((props) => {
  return (
    <StyledFlowCanvas $interactive={props.interactive || false}>
      <DimensionAwareFlow {...props} />
    </StyledFlowCanvas>
  );
});
